export interface ContabilizadoMatch {
  newFileName: string;
  newSupplier: string;
  newInvoiceNumber: string;
  matchReason: "nif+factura" | "filename";
}

export interface ContabilizadoEntry {
  id: string;
  fileName: string;
  supplierTaxId: string;
  invoiceNumber: string;
  supplier: string;
  file: File;
  matchedWith?: ContabilizadoMatch;
}

const USER_SCOPE_FALLBACK = "__anonymous__";

let contabilizadosByUser = new Map<string, ContabilizadoEntry[]>();

const normalize = (s: string): string =>
  s.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

const getScopeKey = (userId?: string | null) => userId || USER_SCOPE_FALLBACK;

const getScopedEntries = (userId?: string | null): ContabilizadoEntry[] =>
  contabilizadosByUser.get(getScopeKey(userId)) ?? [];

const setScopedEntries = (entries: ContabilizadoEntry[], userId?: string | null) => {
  contabilizadosByUser.set(getScopeKey(userId), entries);
};

export const addContabilizado = (entry: ContabilizadoEntry, userId?: string | null) => {
  const current = getScopedEntries(userId);
  // Deduplicate by ID to prevent double-adding from realtime + initial sync
  if (current.some((c) => c.id === entry.id)) return;
  setScopedEntries([...current, entry], userId);
};

export const updateContabilizado = (id: string, updates: Partial<Pick<ContabilizadoEntry, "supplierTaxId" | "invoiceNumber" | "supplier">>, userId?: string | null) => {
  const current = getScopedEntries(userId);
  setScopedEntries(
    current.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    userId,
  );
};

export const removeContabilizado = (id: string, userId?: string | null) => {
  const current = getScopedEntries(userId);
  setScopedEntries(current.filter((c) => c.id !== id), userId);
};

export const getContabilizados = (userId?: string | null): ContabilizadoEntry[] =>
  getScopedEntries(userId);

export const clearContabilizados = (userId?: string | null) => {
  if (typeof userId === "string" && userId.length > 0) {
    contabilizadosByUser.delete(getScopeKey(userId));
    return;
  }
  contabilizadosByUser = new Map<string, ContabilizadoEntry[]>();
};

/** Check if a new invoice matches any contabilizado entry. Also marks the matched entry. */
export const checkDuplicate = (
  supplierTaxId: string,
  invoiceNumber: string,
  fileName: string,
  newSupplier?: string,
  userId?: string | null,
): boolean => {
  const normTaxId = normalize(supplierTaxId);
  const normInvNum = normalize(invoiceNumber);
  const scopedEntries = getScopedEntries(userId);

  for (const c of scopedEntries) {
    let matched = false;
    let reason: "nif+factura" | "filename" = "filename";

    if (
      normTaxId &&
      normInvNum &&
      normalize(c.supplierTaxId) === normTaxId &&
      normalize(c.invoiceNumber) === normInvNum
    ) {
      matched = true;
      reason = "nif+factura";
    } else if (c.fileName === fileName) {
      matched = true;
      reason = "filename";
    }

    if (matched) {
      c.matchedWith = {
        newFileName: fileName,
        newSupplier: newSupplier || "",
        newInvoiceNumber: invoiceNumber,
        matchReason: reason,
      };
      return true;
    }
  }
  return false;
};
